import {
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { format, isAfter } from 'date-fns';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { AppError } from '../../../../../../framework/models/errors';
import { TypeUtils } from '../../../../../../framework/utils/UtilityTypes';
import { useGlobalStateMachine } from '../../../../../GlobalStore';
import {
  MATTER_SUMMARY_CONTEXT_NAMES,
  usePostMatterSummarySearch,
} from '../../../../../hooks/queries/matterSummary';
import useToastHook from '../../../../../hooks/ToastHook';
import { MatterSummary } from '../../../../../models/gen/matterSummary';
import { MatterSummarySearchParam } from '../../../../../models/gen/matterSummarySearchParam';
import { MatterSummarySearchResult } from '../../../../../models/gen/matterSummarySearchResult';
import { Formatter } from '../../../../../utils/FormatUtils';
import { useSizeContext } from '../../../../SizeProvider';
import Result from './Result';
import SearchForm from './SearchForm';

/**
 * 現場検索ダイアログプロパティ
 */
type SearchMatterModalProps = {
  initialDate?: string;
  isOpen: boolean;
  onClose: Runnable;
  onSelect: Consumer<MatterSummary>;
};

/**
 * 現場検索ダイアログ
 * @returns
 */
export const SearchMatterModal: React.FunctionComponent<
  SearchMatterModalProps
> = ({ initialDate, isOpen, onClose, onSelect }) => {
  const { actions } = useGlobalStateMachine();
  const sizes = useSizeContext();
  const form = useForm<MatterSummarySearchParam>({
    defaultValues: { dateFrom: initialDate, dateTo: initialDate },
  });
  const { toast } = useToastHook();

  const queryClient = useQueryClient();
  const matterSummaries = queryClient.getQueryData<Array<MatterSummary>>(
    MATTER_SUMMARY_CONTEXT_NAMES.POST_MATTER_SUMMARY_SEARCH
  );
  const setMatterSummaries = React.useCallback(
    (result: MatterSummarySearchResult) => {
      queryClient.setQueryData(
        MATTER_SUMMARY_CONTEXT_NAMES.POST_MATTER_SUMMARY_SEARCH,
        result.results
      );
    },
    [queryClient]
  );
  const postMatterSummarySearch = usePostMatterSummarySearch();

  React.useEffect(() => {
    form.reset({ dateFrom: initialDate, dateTo: initialDate });
  }, [initialDate]);

  const handleClose = () => {
    onClose();
    // setMatterSummaries({});
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={sizes.fontSize.modalHeader}>
          現場選択
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody fontSize={sizes.fontSize.modalBody}>
          <FormProvider {...form}>
            <SearchForm
              onSearch={(result) => {
                if (
                  TypeUtils.isNilStrict(result.dateFrom) ||
                  TypeUtils.isNilStrict(result.dateTo)
                ) {
                  toast('作業期間を入力してください', 'warning');
                  return;
                }
                if (
                  isAfter(
                    Formatter.createDate(result.dateFrom!),
                    Formatter.createDate(result.dateTo!)
                  )
                ) {
                  toast('作業期間が正しくありません', 'warning');
                  return;
                }
                actions.setGuard(true);
                postMatterSummarySearch.mutate(
                  {
                    ...result,
                    dateFrom: Formatter.date(result.dateFrom, true),
                    dateTo: Formatter.date(result.dateTo, true),
                  },
                  {
                    onSuccess: (data) => {
                      if (TypeUtils.isNonNil(data)) {
                        setMatterSummaries(data);
                      }
                      actions.setGuard(false);
                    },
                    onError: (error) => {
                      toast(
                        error instanceof AppError
                          ? error
                          : '現場検索に失敗しました'
                      );
                      actions.setGuard(false);
                    },
                  }
                );
              }}
            />
          </FormProvider>
          <Divider margin={2} />
          <Result
            onSelect={(matterSummary) => {
              onSelect(matterSummary);
              handleClose();
            }}
            results={{ results: matterSummaries || [] }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
